import {
	Eventcalendar,
	MbscCalendarEvent,
	MbscResource,
	momentTimezone,
	setOptions,
} from '@mobiscroll/react';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import React, { FC, useMemo, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';

import { fetchAllEventTypes } from 'core/API/events';
import { GetUsersList } from 'core/API/users';
// CSS modules
import Loading from 'core/components/Loading/Loading';
import { useAuth } from 'module/auth/context/AuthContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { ScheduleResource, EventTypeResource } from 'types/scheduler';
import { Holiday } from 'types/holidays';

import { useCalendarView } from 'core/hooks/useCalendarView';
import {
	ScheduleEvent,
	ScheduleResource as ScheduleResourceComponent,
} from './ScheduleEvent.component';
import ScheduleEventsHeader from './ScheduleEventsHeader';
import './DesktopPage.style.scss';
import { debounceFunction } from 'core/hooks/useDecbouncedValue';

// setup Mobiscroll Moment plugin
momentTimezone.moment = momentTZ;

interface IDesktopPageSchedule {
	openEditShiftModal: (args: any) => void;
	openAddShiftModal: (args: any) => void;
	allEvents: MbscCalendarEvent[];
	holidays?: Holiday[];
	workplacesData: IWorkplaces;
	updateTimeRange: (firstday: Date, lastday: Date) => void;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	calendarType: string;
	openRequestsModal: (args: any) => void;
	openAddEventModal: (args: any) => void;
	openEditEventModal: (args: any) => void;
	reassignShiftToAnotherUser?: (newShift: MbscCalendarEvent) => void;
	refetch?: any;
	updateOneShiftHandler?: (selectedEvent: MbscCalendarEvent) => void;
	dragTimeStep?: number;
	resourcesOverride?: ScheduleResource[];
	renderHeaderOverride?: () => React.ReactElement | null;
	startOfDay?: string;
	startOfWeek?: string;
	workplaceWorkingHoursStart?: string;
	selectedDate?: Date;
	businessWeekStart?: string;
	onEditShift?: (event: MbscCalendarEvent) => void;
	onDeleteShift?: (event: MbscCalendarEvent) => void;
	onCopyShift?: (event: MbscCalendarEvent) => void;
	onPublish?: () => void;
	canManageShifts?: boolean;
	canDeleteShifts?: boolean;
	onShiftDragUpdate?: (event: any) => void;
}

setOptions({
	theme: 'ios',
	themeVariant: 'light',
});

const DesktopPageSchedule: FC<IDesktopPageSchedule> = ({
	openEditShiftModal,
	openAddShiftModal,
	allEvents,
	holidays = [],
	workplacesData,
	updateTimeRange,
	setCalendarType,
	calendarType,
	openRequestsModal,
	openEditEventModal,
	reassignShiftToAnotherUser: _reassignShiftToAnotherUser,
	refetch,
	updateOneShiftHandler: _updateOneShiftHandler,
	dragTimeStep,
	resourcesOverride,
	renderHeaderOverride,
	startOfDay = '00:00',
	startOfWeek,
	workplaceWorkingHoursStart: _workplaceWorkingHoursStart,
	selectedDate,
	businessWeekStart,
	onEditShift,
	onDeleteShift,
	onCopyShift,
	onPublish,
	canManageShifts = false,
	canDeleteShifts = false,
	onShiftDragUpdate,
}) => {
	const { workplaceId, timezones: _timezones } = useWorkplacesContext();
	const { calendarView, calendarConfig } = useCalendarView();
	const _auth = useAuth();

	// Ref to store the Mobiscroll calendar instance for programmatic scrolling
	const calendarInstanceRef = useRef<any>(null);

	// Calculate business day start and end times in 24:00 format
	const { dayStartTime: _dayStartTime } = useMemo(() => {
		// Start time is the same as startOfDay
		const startTime = startOfDay;

		// End time is 24 hours later (same time next day)
		// For 24-hour format, we use the same time as it represents a full business day cycle

		return {
			dayStartTime: startTime,
		};
	}, [startOfDay]);

	// Modify calendar config to include business day start/end times and week configuration
	const businessDayCalendarConfig = useMemo(() => {
		const config = {
			...calendarConfig,
			timeline: {
				...calendarConfig.timeline,
				rowHeight: 'variable' as const, // Always ensure rowHeight is variable
			},
		};

		// For day view, show the whole week with hourly granularity
		if (calendarView === 'day' && startOfWeek) {
			// Convert start_of_week from day name to day number (0 = Sunday, 1 = Monday, etc.)
			const dayMap: { [key: string]: number } = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			const startDayNum = dayMap[startOfWeek] ?? 1; // Default to Monday if not found

			// For day view, configure to show exactly 7 days starting from organization's start_of_week
			config.timeline = {
				...config.timeline,
				type: 'week',
				startDay: startDayNum,
				endDay: (startDayNum + 6) % 7,
				rowHeight: 'variable',
			};
		}

		// For week and workweek views, use organization's start_of_week and limit to 7 days
		if ((calendarView === 'week' || calendarView === 'workweek') && startOfWeek) {
			// Convert start_of_week from day name to day number (0 = Sunday, 1 = Monday, etc.)
			const dayMap: { [key: string]: number } = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			const startDayNum = dayMap[startOfWeek] ?? 1; // Default to Monday if not found

			// For week view, configure to show exactly 7 days starting from organization's start_of_week
			// Use hourly time slots (60 minutes) instead of daily to show event times accurately
			config.timeline = {
				...config.timeline,
				type: 'week',
				startDay: startDayNum,
				endDay: (startDayNum + 6) % 7,
				rowHeight: 'variable',
				eventList: true,
				timeCellStep: 1440,
				timeLabelStep: 1440,
			};
		}

		return config;
	}, [calendarConfig, calendarView, startOfWeek]);
	// console.log('businessDayCalendarConfig:', businessDayCalendarConfig, selectedDate);
	const { user } = JSON.parse(localStorage.getItem('BYOD-app'));
	const {
		data: _usersData,
		isLoading: isLoadingUsersWorkplace,
		refetch: _refetchUsers,
	} = useQuery(['users', user.id], () => GetUsersList(user.id), {
		select: (data) => {
			function filterAndPreserveDuplicates(users) {
				const uniqueIds = new Set();
				const duplicates = [];

				const uniques = users.filter((user) => {
					if (uniqueIds.has(user.id)) {
						debounceFunction(() => {
							console.group('Duplicate user!');
							console.groupEnd();
						}, 1000);

						duplicates.push(user);
						return false;
					} else {
						uniqueIds.add(user.id);
						return true;
					}
				});

				return { uniques, duplicates };
			}

			const { uniques } = filterAndPreserveDuplicates(data);

			return uniques;
		},
	});

	const { data: eventTypes } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
		enabled: calendarType === 'event',
	});

	const eventResources = useMemo<EventTypeResource[]>(() => {
		const eventsListResource = eventTypes?.items?.map((eventType: any): EventTypeResource => {
			return {
				id: eventType.id,
				name: eventType.type,
			};
		});

		return eventsListResource ?? [];
	}, [eventTypes]);

	const resourcesToRender = useMemo<MbscResource[]>(() => {
		const resources: ScheduleResource[] =
			resourcesOverride ?? (eventResources && eventResources.length ? eventResources : []);
		// Convert to MbscResource format for Mobiscroll compatibility
		return resources.map(
			(resource) =>
				({
					...resource,
					id: resource.id,
					name: resource.name,
					color: resource.color,
				}) as MbscResource
		);
	}, [resourcesOverride, eventResources]);

	const disableDeleteKey = () => {
		refetch();
	};

	// Programmatic scrolling when selectedDate changes
	useEffect(() => {
		if (calendarInstanceRef.current && selectedDate) {
			// Use a small delay to ensure the calendar is fully rendered
			const timer = setTimeout(() => {
				try {
					// Navigate to the selected date without triggering week change
					calendarInstanceRef.current.navigate(selectedDate);
				} catch (error) {
					console.error('Error scrolling to date:', error);
				}
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [selectedDate, calendarView]);

	// console.log('DesktopPageSchedule rendered', selectedDate, businessDayCalendarConfig);

	// Sort events by start time, then by shift id
	const sortedEvents = useMemo(() => {
		return [...allEvents].sort((a, b) => {
			// Compare start times
			const timeA = a.start instanceof Date ? a.start.getTime() : new Date(a.start as any).getTime();
			const timeB = b.start instanceof Date ? b.start.getTime() : new Date(b.start as any).getTime();
			const timeDiff = timeA - timeB;
			
			if (timeDiff !== 0) return timeDiff;
			
			// If start times are the same, sort by shift_id or id
			const idA = (a as any).shift_id || (a as any).id || 0;
			const idB = (b as any).shift_id || (b as any).id || 0;
			return idA - idB;
		});
	}, [allEvents]);

	// Create a map of holiday dates for quick lookup
	const holidayMap = useMemo(() => {
		const map = new Map<string, Holiday>();
		holidays.forEach((holiday) => {
			const dateKey = moment(holiday.holiday_date).format('YYYY-MM-DD');
			map.set(dateKey, holiday);
		});
		// console.log('🗓️ Holiday map created:', Array.from(map.entries()).map(([date, h]) => ({ date, name: h.name })));
		return map;
	}, [holidays]);

	// Reference to the calendar container
	const calendarRef = useRef<HTMLDivElement>(null);

	// Inject holiday indicators into the calendar header after it renders
	useEffect(() => {
		// console.log('🎉 Holiday injection effect running', { 
		// 	hasRef: !!calendarRef.current, 
		// 	holidayCount: holidays.length,
		// 	holidays: holidays.map(h => ({ name: h.name, date: h.holiday_date }))
		// });
		
		if (!calendarRef.current || holidays.length === 0) {
			// console.log('❌ Early return - no ref or no holidays');
			return;
		}

		// Wait for Mobiscroll to render
		const timer = setTimeout(() => {
			// console.log('⏰ Timeout executed, querying DOM...');
			
			// Find all header date elements in the timeline/schedule
			const headerDates = calendarRef.current?.querySelectorAll('.mbsc-timeline-header-date, .mbsc-schedule-header-date');
			
			// console.log('🔍 Query results:', { 
			// 	foundElements: headerDates?.length,
			// 	query: '.mbsc-timeline-header-date, .mbsc-schedule-header-date'
			// });
			
			if (!headerDates || headerDates.length === 0) {
				// Try to find what classes actually exist
				const allElements = calendarRef.current?.querySelectorAll('[class*="header"]');
				// console.log('🔍 All header-like elements:', allElements?.length);
				if (allElements) {
					Array.from(allElements).slice(0, 5).forEach((el: any) => {
						// console.log('  Element classes:', el.className);
					});
				}
				return;
			}

			headerDates.forEach((headerElement: any) => {
				// console.log('📅 Processing header element:', { 
				// 	classes: headerElement.className,
				// 	ariaLabel: headerElement.getAttribute('aria-label'),
				// 	dataDate: headerElement.getAttribute('data-date'),
				// 	textContent: headerElement.textContent?.trim(),
				// 	innerHTML: headerElement.innerHTML.substring(0, 200),
				// 	childrenCount: headerElement.children.length
				// });
				
				let dateToCheck: string | null = null;
				
				// First try data-date attribute
				const dataDate = headerElement.getAttribute('data-date');
				if (dataDate) {
					dateToCheck = moment(dataDate).format('YYYY-MM-DD');
					console.log('  📍 Using data-date:', dataDate, '→', dateToCheck);
				}
				
				// Then try aria-label
				if (!dateToCheck) {
					const ariaLabel = headerElement.getAttribute('aria-label');
					if (ariaLabel) {
						const parsedDate = moment(ariaLabel, ['MMMM D, YYYY', 'MMM D, YYYY', 'M/D/YYYY', 'dddd, MMMM D, YYYY'], true);
						if (parsedDate.isValid()) {
							dateToCheck = parsedDate.format('YYYY-MM-DD');
							// console.log('  📍 Parsed aria-label:', ariaLabel, '→', dateToCheck);
						}
					}
				}
				
				// Try to find a child element with the hidden content that has the full date
				if (!dateToCheck) {
					const hiddenContent = headerElement.querySelector('.mbsc-hidden-content');
					if (hiddenContent) {
						const hiddenText = hiddenContent.textContent?.trim();
						// console.log('  🔍 Found hidden content:', hiddenText);
						
						if (hiddenText) {
							const parsedDate = moment(hiddenText, [
								'dddd, MMMM D, YYYY',
								'MMMM D, YYYY',
								'MMM D, YYYY',
								'M/D/YYYY'
							], true);
							
							if (parsedDate.isValid()) {
								dateToCheck = parsedDate.format('YYYY-MM-DD');
								// console.log('  📍 Parsed from hidden content:', hiddenText, '→', dateToCheck);
							} else {
								// console.log('  ❌ Could not parse hidden content:', hiddenText);
							}
						}
					}
				}
				
				// Fallback: try to extract date from the concatenated text content
				if (!dateToCheck) {
					const textContent = headerElement.textContent?.trim();
					console.log('  🔍 Trying to parse text content:', textContent);
					
					if (textContent) {
						// Try to match "Sunday, March 15, 2026" pattern within the text
						const dateMatch = textContent.match(/([A-Za-z]+,\s+[A-Za-z]+\s+\d+,\s+\d{4})/);
						if (dateMatch) {
							const extractedDate = dateMatch[1];
							console.log('  🔍 Extracted date from text:', extractedDate);
							
							const parsedDate = moment(extractedDate, 'dddd, MMMM D, YYYY', true);
							if (parsedDate.isValid()) {
								dateToCheck = parsedDate.format('YYYY-MM-DD');
								console.log('  📍 Parsed extracted date:', extractedDate, '→', dateToCheck);
							}
						} else {
							console.log('  ❌ Could not extract date pattern from text content');
						}
					}
				}

				if (!dateToCheck) {
					// console.log('  ⚠️ No valid date found for element');
					return;
				}

				const holiday = holidayMap.get(dateToCheck);
				
				// console.log('  🔎 Holiday lookup:', { dateToCheck, found: !!holiday, holidayName: holiday?.name });
				
				if (holiday) {
					// Check if holiday indicator already exists
					if (headerElement.querySelector('.schedule-day-holiday')) {
						// console.log('  ✅ Holiday indicator already exists, skipping');
						return;
					}

					// console.log('  ✨ Injecting holiday indicator:', holiday.name);

					// Create compact holiday indicator
					const holidayDiv = document.createElement('div');
					holidayDiv.className = 'schedule-day-holiday';
					holidayDiv.title = `${holiday.name}${holiday.description ? '\n' + holiday.description : ''}`;
					holidayDiv.innerHTML = `
						<span class="holiday-emoji">🎉</span>
						<span class="holiday-label">${dateToCheck}</span>
						<span class="holiday-name">${holiday.name}</span>
					`;

					// Append to header
					headerElement.appendChild(holidayDiv);
					// console.log('  ✅ Holiday indicator appended to header');
					
					// Add holiday class to the parent column header
					const parentColumn = headerElement.closest('.mbsc-timeline-header-column, .mbsc-schedule-header-column');
					if (parentColumn) {
						parentColumn.classList.add('holiday-column');
						// console.log('  ✅ Added holiday-column class to parent column');
					} else {
						// console.log('  ⚠️ Could not find parent column');
					}
					
					// Find and highlight the entire day column (including the time slots below)
					// For timeline view, find the column by data-date or position
					const allColumns = calendarRef.current?.querySelectorAll('.mbsc-timeline-column, .mbsc-schedule-column');
					if (allColumns) {
						// Try to match by position or find the column that corresponds to this date
						const columnIndex = Array.from(headerElement.parentElement?.children || []).indexOf(headerElement);
						if (columnIndex >= 0) {
							allColumns.forEach((col: any) => {
								// Check if this column corresponds to our date
								const colDate = col.getAttribute('data-date');
								if (colDate) {
									const colDateFormatted = moment(colDate).format('YYYY-MM-DD');
									if (colDateFormatted === dateToCheck) {
										col.classList.add('holiday-day-column');
										// console.log('  ✅ Added holiday-day-column class to body column');
									}
								}
							});
						}
					}
				}
			});
		}, 100);

		return () => clearTimeout(timer);
	}, [holidays, holidayMap, calendarView, businessWeekStart, selectedDate]);

	return isLoadingUsersWorkplace ? (
		<Loading />
	) : (
		<div ref={calendarRef}>
			<Eventcalendar
				ref={(inst) => {
					if (inst) {
						calendarInstanceRef.current = inst;
					}
				}}
				invalidateEvent="strict"
				theme="ios"
				dataTimezone="local"
				displayTimezone="local"
				timezonePlugin={momentTimezone}
				themeVariant="light"
				refDate={businessWeekStart ? moment(businessWeekStart).toDate() : undefined}
				clickToCreate={canManageShifts && calendarType === 'schedule'}
				dragToCreate={false}
				dragToMove={canManageShifts && calendarType === 'schedule'}
				dragToResize={canManageShifts && calendarType === 'schedule' && calendarView !== 'week'}
				// view={calendarConfig}
				view={businessDayCalendarConfig}
				data={sortedEvents}
				eventOverlap={false}
				showEventTooltip={false}
				renderScheduleEvent={(event) => (
				<ScheduleEvent
					calendarType={calendarType}
					calendarView={calendarView}
					data={event}
					onEditClick={canManageShifts ? onEditShift : undefined}
					onDeleteClick={canDeleteShifts ? onDeleteShift : undefined}
					onCopyClick={canManageShifts ? onCopyShift : undefined}
				/>
			)}
			renderResource={(resource) => {
				// Find the original typed resource
				const typedResource = (resourcesOverride ?? eventResources)?.find(
					(r) => r.id === resource.id
				) as ScheduleResource | undefined;
				return (
					<ScheduleResourceComponent
						resource={typedResource || (resource as any)}
						calendarType={calendarType}
					/>
				);
			}}
			resources={resourcesToRender}
			renderHeader={() => {
				if (renderHeaderOverride) {
					return renderHeaderOverride();
				}

				return (
					<ScheduleEventsHeader
						workplacesData={workplacesData}
						workplaceId={workplaceId}
						onSwitchCalendarType={setCalendarType}
						onAddShift={() =>
							openAddShiftModal({
								date: selectedDate || new Date(),
								resource: null,
							})
						}
						onPublish={onPublish}
						canManageShifts={canManageShifts}
					/>
				);
			}}
			dragTimeStep={dragTimeStep ?? 15}
			cssClass={`md-switching-timeline-view-cont md-timeline-height ${calendarView === 'week' || calendarView === 'workweek' ? 'md-week-view' : ''}`}
			timeFormat="h:mm A"
			externalDrop={true}
			onCellClick={(event) => {
				// Prevent adding shifts if user doesn't have permission
				if (!canManageShifts) {
					return;
				}

				// Extract information from resource based on calendar view
				let jobCodeData = null;
				let employeeData = null;

				if (event.resource && resourcesToRender) {
					// Find the resource that matches the clicked resource ID
					const matchedResource = resourcesToRender.find((r) => r.id === event.resource);

					if (matchedResource) {
						// Day view: Extract job code information
						if (matchedResource.jobCode) {
							jobCodeData = {
								id: matchedResource.jobCode.id,
								'@id':
									matchedResource.jobCode['@id'] || `/api/job_codes/${matchedResource.jobCode.id}`,
								label: matchedResource.jobCode.label,
								jobNumber: matchedResource.jobCode.jobNumber,
								backgroundColor: matchedResource.jobCode.backgroundColor,
								textColor: matchedResource.jobCode.textColor,
							};
						}

						// Week view: Extract employee information
						if ((matchedResource as any).employee && !(matchedResource as any).isOpenShift) {
							const employee = (matchedResource as any).employee;
							employeeData = {
								id: employee.id,
								'@id': employee['@id'] || `/api/employees/${employee.id}`,
								firstName: employee.firstName,
								lastName: employee.lastName,
								fullName: `${employee.firstName} ${employee.lastName}`,
							};
						}
					}
				}

				// Pass the event with resource information
				openAddShiftModal({
					...event,
					// Add job code information for day view
					jobCodeFromResource: jobCodeData,
					// Add employee information for week view
					employeeFromResource: employeeData,
				});
			}}
			onEventDoubleClick={({ event }) => {
				if (event.role || !event.scheduled) {
					switch (calendarType) {
						case 'request':
							openRequestsModal(event);
							break;
						case 'event':
							openEditEventModal({
								...event,
								end: new Date(event.end ? event.end.toString().split('+')[0] : '').toString(),
								start: new Date(event.start ? event.start.toString().split('+')[0] : '').toString(),
							});
							break;
						default:
							openEditShiftModal(event);
							break;
					}
				}
			}}
			onEventUpdate={({ event }) => {
				switch (calendarType) {
					case 'request':
						openRequestsModal(event);
						break;
					case 'event':
						openEditEventModal({
							...event,
							fromUtc: moment(event?.start).format('h:mm A'),
							toUtc: moment(event?.end).format('h:mm A'),
						});
						break;
					default:
						// When dragging/resizing a shift, directly call the update API
						if (onShiftDragUpdate) {
							onShiftDragUpdate(event);
						} else {
							// Fallback to opening edit modal if handler not provided
							openEditShiftModal({
								...event,
								start: event.start,
								end: event.end,
							});
						}
						break;
				}
			}}
			onPageChange={(event) => {
				// console.log('DesktopPage onPageChange triggered', {
				// 	eventFirstDay: moment(event.firstDay).format('YYYY-MM-DD'),
				// 	eventLastDay: moment(event.lastDay).format('YYYY-MM-DD'),
				// 	selectedDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD HH:mm') : 'none',
				// });
				// updateTimeRange now has its own validation logic to prevent spurious updates
				updateTimeRange(event.firstDay, event.lastDay);
			}}
			onEventCreate={(event) => {
				// if (event.action === 'drag') return;

				if (event.action === 'externalDrop') {
					openEditShiftModal(event.event);
				} else {
					openAddShiftModal(event);
				}
			}}
			onEventClick={() => {
				disableDeleteKey();
			}}
			/>
		</div>
	);
};

export default DesktopPageSchedule;
